// Beautiful "pre-prompt" that primes the user before triggering the
// native browser permission dialog. Direct-triggering the browser prompt
// on first load nukes the conversion rate — showing a warm custom card
// first, then only calling `Notification.requestPermission()` when the
// user actively opts in, converts 3-5x better.
//
// Displayed once (per browser localStorage flag) after the user logs
// their first meal. Also reachable from Settings → Push toggle.
//
// Bilingual — reads `lang` from `useI18n()` so it follows the user's
// language toggle. When `lang === "en"` the copy + `dir="ltr"`; when
// `lang === "ar"` the copy + `dir="rtl"`. Previously every string was
// hardcoded Arabic which surprised English users.
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X, Utensils, Droplet, LineChart, Sparkles, ShieldCheck, Settings, TrendingUp } from "lucide-react";
import { subscribeToPush } from "@/lib/push";
import { isCapacitorPushNative, isCapacitorAndroid, initIOSPush } from "@/lib/nativePush";
import { ensureAlarmPermission } from "@/lib/localAlarms";
import { getPushPermission, markPushShown, markPushDeclined, markPushEnabled } from "@/lib/pushPromptGate";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n.jsx";

export default function PushPromptCard({ open, onClose, onEnabled }) {
  const { lang } = useI18n();
  const en = lang === "en";
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("main"); // main | confirm | settings

  useEffect(() => {
    if (open) { markPushShown(); setStep("main"); }
  }, [open]);

  const close = () => { onClose && onClose(); };
  const decline = () => { markPushDeclined(); close(); };

  const enable = async () => {
    setBusy(true);
    try {
      if ((await getPushPermission()) === "denied") { setStep("settings"); return; }
      let ok = false;
      if (isCapacitorPushNative()) {
        const res = await initIOSPush();
        ok = !!(res && (res.ok || res.token));
      } else {
        ok = await subscribeToPush();
      }
      if (ok) {
        markPushEnabled();
        toast.success(en ? "Notifications enabled ✓" : "تم تفعيل التنبيهات ✓");
        try { await ensureAlarmPermission(); } catch {}
        onEnabled && onEnabled();
        close();
      } else {
        decline();
      }
    } catch (e) {
      const msg = String(e?.message || "");
      if (msg.includes("iOS")) {
        toast.error(en
          ? "On iPhone: add this app to your Home Screen first (tap the Share button)"
          : "على الآيفون: ثبّت التطبيق من زر المشاركة أولاً"
        );
      } else {
        toast.error(en ? "Couldn't enable notifications" : "تعذّر تفعيل التنبيهات");
      }
      decline();
    } finally {
      setBusy(false);
    }
  };

  const onDismiss = () => {
    if (step === "main") setStep("confirm");
    else decline();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={onDismiss}
          dir={en ? "ltr" : "rtl"}
          data-testid="push-prompt-modal"
        >
          <motion.div
            initial={{ y: 40 }}
            animate={{ y: 0 }}
            exit={{ y: 40 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl"
          >
            {/* Header — orange gradient with bell + close button */}
            <div className="relative orange-gradient text-white px-5 pt-5 pb-6">
              <button
                onClick={onDismiss}
                className={`absolute top-4 ${en ? "right-4" : "left-4"} w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors`}
                aria-label={en ? "Close" : "إغلاق"}
                data-testid="push-prompt-close"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-3 pt-1">
                <div className="relative">
                  <div className="w-14 h-14 rounded-2xl bg-white/25 backdrop-blur-sm flex items-center justify-center border border-white/10">
                    <Bell className="w-7 h-7" strokeWidth={2.4} />
                  </div>
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 border-2 border-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-bold text-white/90 uppercase tracking-wider">
                    {en ? "Calofay Notifications" : "تنبيهات كالوفاي"}
                  </div>
                  <div className="text-lg font-black leading-tight">
                    {en ? "Turn on reminders and hit your goal faster" : "فعّل التذكيرات وحقق هدفك أسرع"}
                  </div>
                </div>
              </div>
            </div>

            {step === "main" && (<>
            {/* Body */}
            <div className="px-5 pt-5 pb-6 space-y-3">
              <Row
                icon={<Utensils className="w-5 h-5 text-brand-500" />}
                title={en ? "Meal reminders" : "تذكير بالوجبات"}
                body={en ? "Breakfast, lunch, and dinner at your preferred times" : "فطور، غداء، وعشاء في أوقاتك المفضّلة"}
              />
              <Row
                icon={<Droplet className="w-5 h-5 text-blue-500" />}
                title={en ? "Hydration nudges" : "ذكّرك بشرب الماء"}
                body={en ? "Every couple of hours through the day — hydration matters" : "كل ساعتين خلال اليوم — الترطيب مهم"}
              />
              <Row
                icon={<LineChart className="w-5 h-5 text-emerald-500" />}
                title={en ? "Weekly weigh-in" : "ميعاد قياس الوزن"}
                body={en ? "A gentle Sunday-morning reminder" : "تذكير أسبوعي كل أحد صباحاً"}
              />
              <Row
                icon={<Sparkles className="w-5 h-5 text-violet-500" />}
                title={en ? "Coach messages" : "رسائل من المدرب"}
                body={en ? "Never miss a reply from our support team" : "لا تفوّت أي رسالة من أخصائية التغذية"}
              />
              <Row
                icon={<TrendingUp className="w-5 h-5 text-orange-500" />}
                title={en ? "Your progress" : "تقدّمك"}
                body={en ? "Weekly summary, streaks & when you'll hit your goal" : "ملخص أسبوعي، إنجازاتك، ومتى توصل لهدفك"}
              />

              {/* Trust cue */}
              <div className="mt-4 flex items-center gap-2 bg-neutral-50 border border-neutral-100 rounded-2xl p-3">
                <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <div className="text-[11px] text-neutral-700 leading-relaxed">
                  {en
                    ? "You can turn these off any time in Settings — we'll never send ads."
                    : "تقدر تعطّلها من الإعدادات في أي وقت — لن نرسل إعلانات أبداً."}
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="px-5 pb-5 space-y-2">
              <button
                onClick={enable}
                disabled={busy}
                className="w-full orange-gradient text-white font-bold py-4 rounded-full shadow-floating active:scale-[0.98] transition-transform text-base disabled:opacity-70"
                data-testid="push-prompt-enable"
              >
                {busy
                  ? (en ? "Enabling…" : "جاري التفعيل...")
                  : (en ? "Turn on notifications" : "فعّل التنبيهات")}
              </button>
              <button
                onClick={onDismiss}
                className="w-full text-neutral-500 text-sm font-semibold py-2 hover:text-neutral-800"
                data-testid="push-prompt-later"
              >
                {en ? "Maybe later" : "لاحقاً"}
              </button>
            </div>
            </>)}
            {step === "confirm" && <ConfirmStep en={en} busy={busy} onEnable={enable} onDecline={decline} />}
            {step === "settings" && <SettingsStep en={en} onDone={decline} />}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Row({ icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-2xl bg-neutral-50 border border-neutral-100 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm">{title}</div>
        <div className="text-[12px] text-neutral-500 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function ConfirmStep({ en, busy, onEnable, onDecline }) {
  return (
    <div className="px-5 pt-5 pb-5 space-y-4 text-center" data-testid="push-confirm-step">
      <h3 className="text-lg font-black text-neutral-900">{en ? "Are you sure? 🥺" : "متأكد؟ 🥺"}</h3>
      <p className="text-sm text-neutral-600 leading-relaxed" data-testid="push-confirm-text">
        {en
          ? "Without notifications we won't be able to remind you to drink water, log your meals, or weigh in, and you'll miss your coach's messages."
          : "بدون التنبيهات ما راح نقدر نذكّرك بشرب الماء وتسجيل وجباتك وقياس وزنك، وراح تفوتك رسائل المدرب."}
      </p>
      <button
        onClick={onEnable}
        disabled={busy}
        className="w-full orange-gradient text-white font-bold py-4 rounded-full shadow-floating active:scale-[0.98] transition-transform disabled:opacity-70"
        data-testid="push-confirm-enable"
      >
        {busy ? (en ? "Enabling…" : "جاري التفعيل...") : (en ? "Turn on reminders" : "فعّل التذكيرات")}
      </button>
      <button
        onClick={onDecline}
        className="w-full text-neutral-500 text-sm font-semibold py-2 hover:text-neutral-800"
        data-testid="push-confirm-decline"
      >
        {en ? "Yes, I don't want reminders" : "نعم، لا أريد التذكيرات"}
      </button>
    </div>
  );
}

function SettingsStep({ en, onDone }) {
  const native = isCapacitorPushNative();
  const android = isCapacitorAndroid();
  const steps = !native
    ? (en
      ? ["Open your browser's site settings", "Find Notifications", "Choose Allow"]
      : ["افتح إعدادات الموقع في المتصفح", "اختر التنبيهات (Notifications)", "اختر سماح (Allow)"])
    : android
      ? (en
        ? ["Open phone Settings", "Apps → Calofay", "Notifications → turn on"]
        : ["افتح الإعدادات في جوالك", "التطبيقات ← Calofay", "الإشعارات ← فعّلها"])
      : (en
        ? ["Open iPhone Settings", "Scroll down and tap Calofay", "Notifications → Allow Notifications"]
        : ["افتح الإعدادات في الآيفون", "انزل واختر Calofay", "الإشعارات ← السماح بالإشعارات"]);
  return (
    <div className="px-5 pt-5 pb-5 space-y-4" data-testid="push-settings-step">
      <div className="flex items-center gap-2">
        <Settings className="w-5 h-5 text-brand-500" />
        <h3 className="text-base font-black text-neutral-900">
          {en ? "Turn on notifications from Settings" : "فعّل التنبيهات من الإعدادات"}
        </h3>
      </div>
      <ol className="space-y-2">
        {steps.map((t, i) => (
          <li key={i} className="flex items-center gap-3 bg-neutral-50 border border-neutral-100 rounded-2xl p-3 text-sm font-semibold">
            <span className="w-7 h-7 rounded-full orange-gradient text-white flex items-center justify-center text-xs font-black flex-shrink-0">{i + 1}</span>
            <span>{t}</span>
          </li>
        ))}
      </ol>
      <button
        onClick={onDone}
        className="w-full bg-neutral-100 text-neutral-800 font-bold py-3.5 rounded-full active:scale-[0.98]"
        data-testid="push-settings-done"
      >
        {en ? "Got it" : "تمام"}
      </button>
    </div>
  );
}
